import { z } from "zod";
import {
  CURRENCIES,
  PRICING_PREFERENCES,
  PRIORITIES,
  REQUEST_STATUSES,
  SCHEDULING_PREFERENCES,
  type Priority,
  type RequestStatus,
} from "../status.js";
import { addressSchema, type AddressResponse } from "./customers.js";

const moneyInput = z
  .union([z.number().finite(), z.string().regex(/^\d{1,12}(\.\d{1,2})?$/)])
  .transform(String);
const timeInput = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
const dateInput = z.string().date();

export const requestCreateSchema = z
  .object({
    customerId: z.string().uuid(),
    /** Category or subcategory id; names are snapshotted server-side. */
    categoryId: z.string().uuid().nullable().optional(),
    shortDescription: z.string().trim().min(1).max(180),
    description: z.string().trim().max(6000).nullable().optional(),
    pricingPreference: z.enum(PRICING_PREFERENCES).default("offer_required"),
    fixedAmount: moneyInput.nullable().optional(),
    fixedAmountTaxIncluded: z.boolean().default(false),
    currency: z.enum(CURRENCIES).default("CHF"),
    schedulingPreference: z.enum(SCHEDULING_PREFERENCES).default("asap"),
    requestedDate: dateInput.nullable().optional(),
    requestedTimeStart: timeInput.nullable().optional(),
    requestedTimeEnd: timeInput.nullable().optional(),
    timeFlexible: z.boolean().default(false),
    priority: z.enum(PRIORITIES).default("normal"),
    /** An existing customer site, or an ad-hoc address below. */
    siteAddressId: z.string().uuid().nullable().optional(),
    siteAddress: addressSchema.nullable().optional(),
    assignedUserIds: z.array(z.string().uuid()).default([]),
    /** Submit to the office immediately instead of saving a draft. */
    submit: z.boolean().default(false),
  })
  .superRefine((value, ctx) => {
    if (
      value.pricingPreference === "fixed_price" &&
      (value.fixedAmount === undefined || value.fixedAmount === null)
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["fixedAmount"],
        message: "Fixed amount is required for fixed-price requests",
      });
    }
    if (value.schedulingPreference === "desired_date" && !value.requestedDate) {
      ctx.addIssue({
        code: "custom",
        path: ["requestedDate"],
        message: "Requested date is required",
      });
    }
    if (
      value.requestedTimeStart &&
      value.requestedTimeEnd &&
      value.requestedTimeEnd <= value.requestedTimeStart
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["requestedTimeEnd"],
        message: "End time must be after start time",
      });
    }
  });
export type RequestCreateDto = z.infer<typeof requestCreateSchema>;

export const requestUpdateSchema = z.object({
  version: z.number().int().min(1),
  customerId: z.string().uuid().optional(),
  categoryId: z.string().uuid().nullable().optional(),
  shortDescription: z.string().trim().min(1).max(180).optional(),
  description: z.string().trim().max(6000).nullable().optional(),
  pricingPreference: z.enum(PRICING_PREFERENCES).optional(),
  fixedAmount: moneyInput.nullable().optional(),
  fixedAmountTaxIncluded: z.boolean().optional(),
  currency: z.enum(CURRENCIES).optional(),
  schedulingPreference: z.enum(SCHEDULING_PREFERENCES).optional(),
  requestedDate: dateInput.nullable().optional(),
  requestedTimeStart: timeInput.nullable().optional(),
  requestedTimeEnd: timeInput.nullable().optional(),
  timeFlexible: z.boolean().optional(),
  priority: z.enum(PRIORITIES).optional(),
  siteAddressId: z.string().uuid().nullable().optional(),
  siteAddress: addressSchema.nullable().optional(),
  assignedUserIds: z.array(z.string().uuid()).optional(),
});
export type RequestUpdateDto = z.infer<typeof requestUpdateSchema>;

export const requestStatusSchema = z
  .object({
    version: z.number().int().min(1),
    status: z.enum(REQUEST_STATUSES),
    /** Required when declining or cancelling. */
    reason: z.string().trim().max(1000).optional(),
  })
  .superRefine((value, ctx) => {
    if (
      (value.status === "declined" || value.status === "cancelled") &&
      !value.reason
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["reason"],
        message: "A reason is required",
      });
    }
  });
export type RequestStatusDto = z.infer<typeof requestStatusSchema>;

export const requestListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  q: z.string().trim().max(120).optional(),
  status: z.enum(REQUEST_STATUSES).optional(),
  /** "open" = not converted/declined/cancelled. */
  scope: z.enum(["open", "closed", "all"]).default("all"),
  priority: z.enum(PRIORITIES).optional(),
  customerId: z.string().uuid().optional(),
  projectId: z.string().uuid().optional(),
  assignedTo: z.string().uuid().optional(),
  mine: z
    .enum(["true", "false"])
    .transform((v) => v === "true")
    .optional(),
  sortField: z
    .enum(["number", "createdAt", "priority", "requestedDate", "status"])
    .default("createdAt"),
  sortOrder: z.enum(["asc", "desc"]).default("desc"),
});
export type RequestListQuery = z.infer<typeof requestListQuerySchema>;

export interface AttachmentResponse {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  width?: number;
  height?: number;
  caption?: string;
  thumbnailUrl?: string;
  downloadUrl: string;
  uploadedById: string;
  createdAt: string;
}

export interface RequestResponse {
  id: string;
  number: string;
  customer: {
    id: string;
    number: string;
    displayName: string;
    phone?: string;
    email?: string;
  };
  project?: { id: string; number: string; title: string };
  offers: Array<{
    id: string;
    number: string;
    displayNumber: string;
    status: string;
  }>;
  invoices: Array<{ id: string; number?: string; status: string }>;
  categoryId?: string;
  categoryName: string;
  subcategoryName?: string;
  shortDescription: string;
  description?: string;
  pricingPreference: string;
  fixedAmount?: string;
  fixedAmountTaxIncluded: boolean;
  currency: string;
  schedulingPreference: string;
  requestedDate?: string;
  requestedTimeStart?: string;
  requestedTimeEnd?: string;
  timeFlexible: boolean;
  priority: Priority;
  siteAddressId?: string;
  siteAddress?: AddressResponse;
  status: RequestStatus;
  statusReason?: string;
  assignedUsers: Array<{ id: string; firstName: string; lastName: string }>;
  attachments: AttachmentResponse[];
  createdById: string;
  createdBy: { id: string; name: string };
  submittedAt?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}
